import React, { Component } from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { Route } from 'react-router-dom';
import PropTypes from 'prop-types';
import {
  Button, Dimmer, Header, Icon, Loader, Menu, Modal, Table,
} from 'semantic-ui-react';

import { errorType, recognitionType } from '../types';

import UploadModal from './uploadModal';
import ConsoleErrorMessage from '../utils/console-error-message';
import RefreshButton from '../utils/refresh-button';
import RemeetingTimestamp from '../utils/remeeting-timestamp';

import {
  deleteRecognition,
} from '../../modules/recognition';

import {
  clearError,
  listRecognitions,
  pageRecognitions,
  updateRecognitions,
} from '../../modules/recognitions';

export const Processing = recog => recog.status && recog.status !== 'completed' && recog.status !== 'failed';

class RecognitionTable extends Component {
  constructor() {
    super();

    this.state = {
      deletedIds: [],
      utc: false,
      filter: undefined,
    };
  }

  componentDidMount() {
    const { connectedClearError, connectedListRecognitions } = this.props;
    connectedClearError();
    connectedListRecognitions();
  }

  toggleUtc = (event) => {
    event.target.blur();
    this.setState(prevState => ({ utc: !prevState.utc }));
  }

  statusFilterChange = (event, data) => {
    const { connectedListRecognitions } = this.props;
    const newStatus = data.value === 'all' ? undefined : data.value;
    connectedListRecognitions({ status: newStatus })
      .then(() => this.setState({ filter: newStatus }));
  }

  loadMoreRecognitions = () => {
    const { connectedPageRecognitions } = this.props;
    const { filter } = this.state;
    connectedPageRecognitions({ status: filter });
  }

  deleteRecognition = (event, data) => {
    const { connectedDeleteRecognition } = this.props;
    event.preventDefault();
    event.stopPropagation();
    const id = data.className.slice(16);
    this.setState(prevState => ({ deletedIds: prevState.deletedIds.concat([id]) }));
    connectedDeleteRecognition(id);
  }

  /* First row of the page header
   *
   * Creates the title and upload button
   * */
  renderPageTitleAndPrimaryAction = () => {
    const { recognitions } = this.props;
    const { filter } = this.state;
    return (
      <Menu secondary borderless className="re is-header-row">

        {/* title */}
        <Header as="h1" className="re is-header-item">
          Recognitions
        </Header>

        <Menu.Menu position="right">
          { recognitions.length === 0 && !filter ? null : <UploadModal /> }
        </Menu.Menu>
      </Menu>
    );
  }

  /* Second row of the page header
   *
   * Organizes table actions, which are currently
   * limited to date and status filter
   *
   * TODO: Ensure filter values can actually be adjusted and are preserved.
   * Currently the filter dropdowns close when clicking inside the dropdown.
   * The filter dropdowns should only close on a click outside
   * of the dropdown area.
   * */
  renderTableFilters = () => {
    const { connectedUpdateRecognitions, recognitions } = this.props;
    const { filter } = this.state;
    return (
      <Menu secondary borderless className="re is-header-row is-alignItems-center">
        <div className="re is-flex is-flex-row">
          <Button.Group className="re is-padding-halfEm" size="small">
            <Button
              active={!filter}
              name="all"
              content="all"
              value="all"
              onClick={this.statusFilterChange}
            />
            <Button
              active={filter === 'waiting'}
              name="waiting"
              content="waiting"
              value="waiting"
              onClick={this.statusFilterChange}
            />
            <Button
              active={filter === 'processing'}
              name="processing"
              content="processing"
              value="processing"
              onClick={this.statusFilterChange}
            />
            <Button
              active={filter === 'completed'}
              name="completed"
              content="completed"
              value="completed"
              onClick={this.statusFilterChange}
            />
            <Button
              active={filter === 'failed'}
              name="failed"
              content="failed"
              value="failed"
              onClick={this.statusFilterChange}
            />
          </Button.Group>
        </div>
        <Menu.Menu position="right">
          <RefreshButton
            collection={recognitions}
            callback={() => connectedUpdateRecognitions({ status: filter })}
            delay={10000}
            filter={Processing}
          />
        </Menu.Menu>
      </Menu>
    );
  }

  /* Table Rows
   *
   * Iterates through a list of recognitions and formats
   * table rows with the necessary information
   *
   * */
  renderTableRows = (history) => {
    const { deleteRecognitionRequestOut, recognitions } = this.props;
    const { deletedIds, utc } = this.state;
    return recognitions.map(recognition => (
      <Table.Row
        key={recognition.id}
        onClick={() => { history.push(`/recognitions/${recognition.id}`); }}
      >
        <Table.Cell>
          { utc ? recognition.created : <RemeetingTimestamp date={recognition.created} /> }
        </Table.Cell>
        <Table.Cell>
          { this.renderTableRowStatus(recognition.status) }
        </Table.Cell>
        <Table.Cell>
          { recognition.id }
        </Table.Cell>
        <Table.Cell>
          <Button
            className={`j-recognitionId-${recognition.id}`}
            onClick={this.deleteRecognition}
            loading={deletedIds.indexOf(recognition.id) !== -1
                  && deleteRecognitionRequestOut}
            color="red"
            disabled={recognition.status === 'waiting' || recognition.status === 'processing'}
          >
              Delete
          </Button>
        </Table.Cell>
      </Table.Row>
    )).reverse();
  }

  renderTableRowStatus = (status) => {
    if (status === 'failed') {
      return (
        <div className="re is-flex is-flex-align-baseline">
          <Icon color="red" name="times circle" />
          { status }
        </div>
      );
    }
    if (status === 'waiting') {
      return (
        <div className="re is-flex is-flex-align-baseline">
          <Icon color="orange" name="circle" />
          { status }
        </div>
      );
    }
    if (status === 'processing') {
      return (
        <div className="re is-flex is-flex-align-baseline">
          <Icon color="yellow" name="circle" />
          { status }
        </div>
      );
    }
    if (status === 'completed') {
      return (
        <div className="re is-flex is-flex-align-baseline">
          <Icon color="green" name="check circle" />
          { status }
        </div>
      );
    }
    return status;
  }

  /* Table
   *
   * Formats the table along with the column headers
   * */
  renderTable = () => {
    const { utc } = this.state;
    return (
      <Route render={({ history }) => (
        <Table selectable className="re is-table recognitions-table">
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell width="five">
                Created
                <Button inverted={!utc} secondary size="mini" onClick={(event) => { this.toggleUtc(event); }} className="re is-marginLeft-8px">
                  UTC
                </Button>
              </Table.HeaderCell>
              <Table.HeaderCell width="three">
                  Status
              </Table.HeaderCell>
              <Table.HeaderCell>
                  Id
              </Table.HeaderCell>
              <Table.HeaderCell />
            </Table.Row>
          </Table.Header>
          <Table.Body>
            { this.renderTableRows(history) }
          </Table.Body>
        </Table>
      )}
      />
    );
  }

  /* Empty state
   *
   * Formats the empty state
   * */
  renderEmptyState = () => (
    <div className="re is-flexGrow-1  is-flex is-justifyContent-center is-alignItems-center">
      <div className="re is-textAlign-center is-padding-bottom-16px is-padding-top-16px">
        <Header as="h2">
            Get Started
        </Header>
        <p>
            Upload a media file or url to create a recognitions job.
        </p>
        <UploadModal />
      </div>
    </div>
  )

  renderErrorModal = () => {
    const { connectedClearError, error } = this.props;
    return (
      <Modal
        content={<ConsoleErrorMessage error={error} />}
        onClose={connectedClearError}
        open={!!error}
      >
        <Modal.Header>Error</Modal.Header>
        <Modal.Content>
          <ConsoleErrorMessage error={error} />
        </Modal.Content>
      </Modal>
    );
  }

  /* Primary render function */
  render() {
    const {
      listRecognitionsRequestOut, pageRecognitionsRequestOut, recognitions, recognitionsLoaded,
    } = this.props;
    const { filter } = this.state;
    /* Check to see there are truly no recognitions */
    const isEmpty = recognitions.length === 0 && !filter;
    return (
      <div className="re is-main is-background-black-3 is-full-height is-flex is-flexDirection-column">
        <div className="re is-header">
          { this.renderPageTitleAndPrimaryAction() }
          { isEmpty ? null : this.renderTableFilters() }
        </div>
        { isEmpty && listRecognitionsRequestOut && <Dimmer active><Loader /></Dimmer> }
        { this.renderErrorModal() }
        { isEmpty ? this.renderEmptyState() : this.renderTable() }
        { !isEmpty && !recognitionsLoaded && (
        <Button
          loading={pageRecognitionsRequestOut}
          onClick={() => { this.loadMoreRecognitions(); }}
        >
          Load More Recognitions
        </Button>
        ) }
      </div>
    );
  }
}

RecognitionTable.propTypes = {
  connectedClearError: PropTypes.func.isRequired,
  connectedDeleteRecognition: PropTypes.func.isRequired,
  deleteRecognitionRequestOut: PropTypes.bool.isRequired,
  error: errorType,
  connectedListRecognitions: PropTypes.func.isRequired,
  connectedPageRecognitions: PropTypes.func.isRequired,
  listRecognitionsRequestOut: PropTypes.bool.isRequired,
  pageRecognitionsRequestOut: PropTypes.bool.isRequired,
  recognitions: PropTypes.arrayOf(recognitionType).isRequired,
  recognitionsLoaded: PropTypes.bool.isRequired,
  connectedUpdateRecognitions: PropTypes.func.isRequired,
};

RecognitionTable.defaultProps = {
  error: undefined,
};

const mapStateToProps = state => ({
  deleteRecognitionRequestOut: state.recognition.deleteRecognitionRequestOut,
  error: state.recognitions.error,
  listRecognitionsRequestOut: state.recognitions.listRecognitionsRequestOut,
  pageRecognitionsRequestOut: state.recognitions.pageRecognitionsRequestOut,
  recognitions: state.recognitions.recognitions,
  recognitionsLoaded: state.recognitions.recognitionsLoaded,
});

const mapDispatchToProps = dispatch => bindActionCreators({
  connectedClearError: clearError,
  connectedDeleteRecognition: deleteRecognition,
  connectedListRecognitions: listRecognitions,
  connectedPageRecognitions: pageRecognitions,
  connectedUpdateRecognitions: updateRecognitions,
}, dispatch);

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(RecognitionTable);
